import { useCallback, useEffect, useState } from 'react';
import { axiosClient } from '../../auth-lib';
import AdminLayout from '../components/AdminLayout';
import {
  Button, Card, EmptyState, Input, Modal, Select, Skeleton, StatusPill, Table, Tabs, useToast,
} from '../../design-system';
import { PLANS, formatPrice, planName, seatRangeLabel } from '../../lib/plans';
import '../styles/AdminSubscriptions.css';

// ─── The super admin's side of subscriptions ────────────────────────────────
//
// Two questions, so two tabs: "who is waiting on me" (the queue) and "where does
// every firm stand" (the register). Approving from here is what opens the
// platform for a firm — the gate is server-side in both services, so nothing
// here is cosmetic.

const STATUS_TONE = {
  active: 'completed',
  pending: 'review',
  none: 'todo',
  rejected: 'on-hold',
  suspended: 'on-hold',
  cancelled: 'on-hold',
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

/** "3 of 5" / "12 of unlimited" — the figure that makes a decision possible. */
function SeatCell({ seats }) {
  if (!seats) return <span className="asub-muted">—</span>;
  const over = seats.limit != null && seats.used > seats.limit;
  return (
    <span className={over ? 'asub-over' : ''}>
      {seats.used} of {seats.unlimited ? 'unlimited' : seats.limit || '—'}
    </span>
  );
}

function DecideModal({ request, onClose, onDone }) {
  const toast = useToast();
  // Defaults to what was ASKED for. The super admin overrides when the ask does
  // not match the firm — a firm of four asking for Large gets Small.
  const [plan, setPlan] = useState(request?.plan || 'small');
  const [seatOverride, setSeatOverride] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  if (!request) return null;
  const fits = request.seats ? PLANS.find((p) => p.id === plan) : null;
  const seatsUsed = request.seats?.used ?? 0;
  const tooSmall = fits && fits.maxSeats != null && seatsUsed > fits.maxSeats;

  const decide = async (approve) => {
    if (!approve && !note.trim()) {
      toast.danger('Give a reason — the firm sees it, and without one they will simply ask again.');
      return;
    }
    setBusy(true);
    try {
      await axiosClient.post(`/subscription/admin/requests/${request._id}/decide`, {
        approve,
        plan: approve ? plan : undefined,
        seatLimitOverride: approve && seatOverride ? Number(seatOverride) : undefined,
        note,
      });
      toast.success(approve ? `${request.organizationName} is now active` : 'Request refused');
      onDone();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not record the decision');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Decide: ${request.organizationName}`}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={() => decide(false)} disabled={busy}>Refuse</Button>
          <Button variant="primary" size="sm" onClick={() => decide(true)} disabled={busy}>Approve</Button>
        </>
      }
    >
      <div className="asub-decide">
        <dl className="asub-facts">
          <div><dt>Asked for</dt><dd>{planName(request.plan)}</dd></div>
          <div><dt>Says they need</dt><dd>{request.requestedSeats || '—'} users</dd></div>
          <div><dt>Users today</dt><dd><SeatCell seats={request.seats} /></dd></div>
          <div><dt>Requested</dt><dd>{fmtDate(request.requestedAt)} by {request.requestedByName || '—'}</dd></div>
        </dl>
        {request.note && <p className="asub-note">“{request.note}”</p>}

        <Select label="Grant this plan" value={plan} onChange={(e) => setPlan(e.target.value)}>
          {PLANS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {seatRangeLabel(p.id)} — {formatPrice(p.priceMonthly)}/mo
            </option>
          ))}
        </Select>

        {/* The firm already has more people than the plan allows. Nothing is
            blocked — they simply cannot add more — but deciding without knowing
            it is how a firm gets approved onto a plan it has already outgrown. */}
        {tooSmall && (
          <p className="asub-warn">
            This firm already has {seatsUsed} users, which is more than {planName(plan)} allows.
            They will not be able to add anyone until the plan is raised, or you grant extra seats below.
          </p>
        )}

        <Input
          label="Extra seats (optional)"
          type="number"
          min="1"
          value={seatOverride}
          onChange={(e) => setSeatOverride(e.target.value)}
          placeholder="Leave blank to use the plan's own limit"
        />
        <Input
          label="Note (required to refuse)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="The firm sees this"
        />
      </div>
    </Modal>
  );
}

function ManageModal({ row, onClose, onDone }) {
  const toast = useToast();
  const [status, setStatus] = useState(row?.subscription?.status || 'none');
  const [plan, setPlan] = useState(row?.subscription?.plan || '');
  const [seatOverride, setSeatOverride] = useState(
    row?.subscription?.seatLimitOverride != null ? String(row.subscription.seatLimitOverride) : ''
  );
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  if (!row) return null;

  const save = async () => {
    setBusy(true);
    try {
      await axiosClient.put(`/subscription/admin/organizations/${row.id}`, {
        status,
        plan: plan || null,
        seatLimitOverride: seatOverride ? Number(seatOverride) : null,
        note,
      });
      toast.success(`${row.name} updated`);
      onDone();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not update the subscription');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Subscription: ${row.name}`}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={save} disabled={busy}>Save</Button>
        </>
      }
    >
      <div className="asub-decide">
        <Select label="Plan" value={plan} onChange={(e) => setPlan(e.target.value)}>
          <option value="">— none —</option>
          {PLANS.map((p) => (
            <option key={p.id} value={p.id}>{p.name} — {seatRangeLabel(p.id)}</option>
          ))}
        </Select>
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          {/* `pending` and `rejected` are outcomes of the request flow, not
              states to set by hand — the server refuses them here too. */}
          <option value="active">Active — the firm can use the platform</option>
          <option value="suspended">Suspended — access stops</option>
          <option value="cancelled">Cancelled — access stops</option>
          <option value="none">None — no subscription</option>
        </Select>
        <Input
          label="Extra seats (optional)"
          type="number"
          min="1"
          value={seatOverride}
          onChange={(e) => setSeatOverride(e.target.value)}
          placeholder="Leave blank to use the plan's own limit"
        />
        <Input
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="The firm sees this — say why"
        />
        <p className="asub-muted">
          Suspending takes effect within about 15 seconds; both services cache a
          firm's standing for that long.
        </p>
      </div>
    </Modal>
  );
}

function AdminSubscriptions() {
  const toast = useToast();
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [deciding, setDeciding] = useState(null);
  const [managing, setManaging] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, o] = await Promise.all([
        axiosClient.get('/subscription/admin/requests', { params: { status: 'all' } }),
        axiosClient.get('/subscription/admin/organizations'),
      ]);
      setRequests(r.data);
      setOrgs(o.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const pending = requests.filter((r) => r.status === 'pending');
  const decided = requests.filter((r) => r.status !== 'pending');

  const requestColumns = (withActions) => [
    { key: 'organizationName', header: 'Firm', render: (r) => <strong>{r.organizationName}</strong> },
    { key: 'plan', header: 'Asked for', render: (r) => planName(r.plan) },
    { key: 'requestedSeats', header: 'Needs', render: (r) => (r.requestedSeats ? `${r.requestedSeats} users` : '—') },
    { key: 'seats', header: 'Users today', render: (r) => <SeatCell seats={r.seats} /> },
    { key: 'requestedAt', header: 'Requested', render: (r) => fmtDate(r.requestedAt) },
    ...(withActions
      ? [{
          key: 'actions',
          header: '',
          render: (r) => (
            <Button variant="primary" size="sm" onClick={() => setDeciding(r)}>Decide</Button>
          ),
        }]
      : [{
          key: 'outcome',
          header: 'Outcome',
          render: (r) => (
            <span>
              <StatusPill status={r.status === 'approved' ? 'completed' : 'on-hold'} />{' '}
              {r.decidedPlan ? planName(r.decidedPlan) : ''}
              {r.decisionNote ? <span className="asub-muted"> — {r.decisionNote}</span> : null}
            </span>
          ),
        }]),
  ];

  const orgColumns = [
    { key: 'name', header: 'Firm', render: (o) => <strong>{o.name}</strong> },
    {
      key: 'status',
      header: 'Status',
      render: (o) => (
        <span>
          <StatusPill status={STATUS_TONE[o.subscription.status] || 'todo'} />{' '}
          {o.subscription.status}
        </span>
      ),
    },
    { key: 'plan', header: 'Plan', render: (o) => (o.subscription.plan ? planName(o.subscription.plan) : '—') },
    { key: 'seats', header: 'Seats', render: (o) => <SeatCell seats={o.seats} /> },
    { key: 'activatedAt', header: 'Customer since', render: (o) => fmtDate(o.subscription.activatedAt) },
    {
      key: 'open',
      header: 'Waiting',
      render: (o) => (o.subscription.openRequest ? planName(o.subscription.openRequest.plan) : '—'),
    },
    {
      key: 'actions',
      header: '',
      render: (o) => <Button variant="secondary" size="sm" onClick={() => setManaging(o)}>Manage</Button>,
    },
  ];

  return (
    <AdminLayout title="Subscriptions">
      {loading ? (
        <Card><Skeleton.Text lines={8} /></Card>
      ) : (
        <>
          <div className="asub-kpis">
            <Card className="asub-kpi">
              <span className="asub-kpi-label">Awaiting a decision</span>
              <strong className={pending.length ? 'asub-kpi-alert' : ''}>{pending.length}</strong>
            </Card>
            <Card className="asub-kpi">
              <span className="asub-kpi-label">Active firms</span>
              <strong>{orgs.filter((o) => o.subscription.status === 'active').length}</strong>
            </Card>
            <Card className="asub-kpi">
              <span className="asub-kpi-label">Never subscribed</span>
              <strong>{orgs.filter((o) => o.subscription.status === 'none').length}</strong>
            </Card>
            <Card className="asub-kpi">
              <span className="asub-kpi-label">Stopped</span>
              <strong>
                {orgs.filter((o) => ['suspended', 'cancelled'].includes(o.subscription.status)).length}
              </strong>
            </Card>
          </div>

          <Tabs
            tabs={[
              { id: 'pending', label: `Requests (${pending.length})` },
              { id: 'organizations', label: `All firms (${orgs.length})` },
              { id: 'history', label: `Decided (${decided.length})` },
            ]}
            activeTab={tab}
            onChange={setTab}
          />

          {tab === 'pending' && (
            pending.length === 0 ? (
              <EmptyState title="Nothing waiting" description="No firm is currently asking for a subscription." />
            ) : (
              <Card><Table columns={requestColumns(true)} data={pending} rowKey={(r) => r._id} /></Card>
            )
          )}

          {tab === 'organizations' && (
            <Card><Table columns={orgColumns} data={orgs} rowKey={(o) => o.id} /></Card>
          )}

          {tab === 'history' && (
            decided.length === 0 ? (
              <EmptyState title="No decisions yet" description="Approved and refused requests appear here." />
            ) : (
              <Card><Table columns={requestColumns(false)} data={decided} rowKey={(r) => r._id} /></Card>
            )
          )}
        </>
      )}

      {deciding && (
        <DecideModal
          request={deciding}
          onClose={() => setDeciding(null)}
          onDone={() => { setDeciding(null); load(); }}
        />
      )}
      {managing && (
        <ManageModal
          row={managing}
          onClose={() => setManaging(null)}
          onDone={() => { setManaging(null); load(); }}
        />
      )}
    </AdminLayout>
  );
}

export default AdminSubscriptions;
